import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Student, Plus, Check, Trash, Sparkle } from "@phosphor-icons/react";

export const StudentNameDialog = ({ open, onOpenChange }) => {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const learners = user?.learners || [];
  const active = user?.student_name;

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Please enter a name."); return; }
    setBusy(true);
    try {
      await api.post("/profile/learners", { name: trimmed });
      await checkAuth();
      setName("");
      toast.success(`${trimmed} added! 🎉`);
    } catch { toast.error("Could not add. Try again."); }
    finally { setBusy(false); }
  };

  const makeActive = async (n) => {
    if (n === active) return;
    setBusy(true);
    try {
      await api.post("/profile/active-learner", { name: n });
      await checkAuth();
      toast.success(`Certificates now print for ${n}.`);
    } catch { toast.error("Could not switch learner."); }
    finally { setBusy(false); }
  };

  const remove = async (n) => {
    setBusy(true);
    try {
      await api.delete("/profile/learners", { data: { name: n } });
      await checkAuth();
      toast.success(`${n} removed.`);
    } catch { toast.error("Could not remove."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="student-name-dialog">
        <DialogHeader>
          <div className="grid place-items-center w-14 h-14 rounded-2xl bg-brand-sky/15 mb-2">
            <Student size={30} weight="duotone" className="text-brand-sky" />
          </div>
          <DialogTitle className="font-heading text-2xl text-ink">Who's learning?</DialogTitle>
          <DialogDescription className="font-body text-ink-soft">
            Add each child by name and pick who's active. The active learner's name is printed on every certificate they earn, from Pre-K to 5th grade.
          </DialogDescription>
        </DialogHeader>

        {learners.length > 0 && (
          <div className="space-y-2 mt-1 max-h-56 overflow-y-auto" data-testid="learner-list">
            {learners.map((l) => {
              const isActive = l === active;
              return (
                <div key={l} className={`flex items-center gap-3 rounded-2xl border-2 p-3 transition-colors ${isActive ? "border-brand-sky bg-brand-sky/5" : "border-slate-900/10"}`} data-testid={`learner-row-${l}`}>
                  <button onClick={() => makeActive(l)} disabled={busy} className="flex items-center gap-3 flex-1 text-left" data-testid={`learner-select-${l}`}>
                    <span className={`grid place-items-center w-8 h-8 rounded-full text-white shrink-0 ${isActive ? "bg-brand-sky" : "bg-slate-300"}`}>
                      {isActive ? <Check size={16} weight="bold" /> : <Student size={16} weight="duotone" />}
                    </span>
                    <span className="font-heading font-semibold text-ink">{l}</span>
                    {isActive && <span className="text-xs font-bold text-brand-sky uppercase tracking-wide">Active</span>}
                  </button>
                  <button onClick={() => remove(l)} disabled={busy} className="text-ink-soft hover:text-brand-coral p-1" data-testid={`learner-remove-${l}`} aria-label={`Remove ${l}`}>
                    <Trash size={18} weight="duotone" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <Input
            autoFocus={learners.length === 0}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Add a child's name…"
            className="rounded-xl py-5"
            data-testid="student-name-input"
          />
          <Button onClick={add} disabled={busy} className="rounded-xl bg-brand-sky hover:bg-brand-skylight text-white font-bold px-4 shrink-0" data-testid="student-name-save-btn">
            <Plus size={20} weight="bold" className="mr-1" /> Add
          </Button>
        </div>

        {learners.length > 0 && (
          <Button onClick={() => onOpenChange(false)} variant="ghost" className="rounded-full font-semibold mt-1" data-testid="learner-done-btn">
            <Sparkle size={18} weight="fill" className="mr-2 text-brand-sun" /> Done
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
